import { Link } from "react-router-dom";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Badge } from "@jambushrushti/components/ui/badge";
import { SLAIndicator } from "./SLAIndicator";
import { Ticket } from "@jambushrushti/hooks/useTickets";
import { format } from "date-fns";
import { ChevronRight, User } from "lucide-react";

interface TicketCardProps {
  ticket: Ticket;
  assigneeName?: string;
}

const priorityColors = {
  low: "bg-gray-100 text-gray-700 border-gray-200",
  medium: "bg-blue-100 text-blue-700 border-blue-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  critical: "bg-red-100 text-red-700 border-red-200",
};

const statusColors = {
  open: "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  on_hold: "bg-gray-100 text-gray-700",
  resolved: "bg-green-100 text-green-700",
  closed: "bg-purple-100 text-purple-700",
};

const statusLabels = {
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  resolved: "Resolved",
  closed: "Closed",
};

export function TicketCard({ ticket, assigneeName }: TicketCardProps) {
  return (
    <Link to={`/tickets/${ticket.id}`}>
      <Card className="group cursor-pointer transition-shadow hover:shadow-md">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-center gap-2">
                <span className="font-mono text-xs text-muted-foreground">
                  {ticket.ticket_number}
                </span>
                <Badge
                  variant="outline"
                  className={priorityColors[ticket.priority]}
                >
                  {ticket.priority.charAt(0).toUpperCase() +
                    ticket.priority.slice(1)}
                </Badge>
                <Badge className={statusColors[ticket.status]}>
                  {statusLabels[ticket.status]}
                </Badge>
              </div>

              <h3 className="truncate font-medium text-foreground transition-colors group-hover:text-primary">
                {ticket.title}
              </h3>

              {ticket.description && (
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                  {ticket.description}
                </p>
              )}

              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                {ticket.category && (
                  <span className="rounded bg-muted px-2 py-0.5">
                    {ticket.category.name}
                  </span>
                )}
                <span>
                  {format(new Date(ticket.created_at), "MMM dd, yyyy HH:mm")}
                </span>
                {ticket.assigned_to && (
                  <span className="flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-green-700">
                    <User className="h-3 w-3" />
                    {assigneeName || "Assigned"}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <SLAIndicator
                deadline={ticket.sla_deadline}
                status={ticket.status}
              />
              <ChevronRight className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
